import React, { useState, useCallback, useEffect } from 'react';

import {
  Box,
  Divider,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  styled,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from '../../common/CommonSectionHeader';
import { checkValueExists } from '../../../utils/dataSchema';

import { useAppContextFeatures } from '../../../context/AppContext';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useLocations, locationTypes, targetTypes } from '../../../hooks/api/useLocations';
import { useRegions, regionTypes } from '../../../hooks/api/useRegions';

import { initialValues, validationSchema } from '../../../schema/locations/locationSchema';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { useTranslations } from '../../../hooks/useTranslations';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export default function NewLocation({ routeType }) {
  const { t } = useTranslations({ ns: ['common', '@location'] });

  const { promptMessage } = useSystemPrompt();
  const { isLoading, create } = useLocations({
    locationType: locationTypes.location,
    targetType: targetTypes.location,
  });
  const { setSystemLoading } = useAppContextFeatures();

  const navigate = useNavigate();
  const { getRoute } = useSystemRoutes(routeType);
  const { getHtmlHead } = useSystemHead(routeType);

  const { list: listRegions, regionTypes } = useRegions({});
  const [dataCountries, setDataCountries] = useState([]);
  const [dataStates, setDataStates] = useState([]);
  const [dataAreas, setDataAreas] = useState([]);

  const fetchDataCountry = async () => {
    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'country_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.country;

      const { data } = await listRegions(props);

      // console.log('re', data);

      setDataCountries(data?.countries);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataStates = async (countryId) => {
    setDataStates([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'state_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.state;
      props.countryId = countryId;

      const { data } = await listRegions(props);

      setDataStates(data?.states);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataAreas = async (stateId) => {
    setDataAreas([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'area_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.area;
      props.stateId = stateId;

      const { data } = await listRegions(props);

      setDataAreas(data?.areas);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const onSubmit = async (currentValues) => {
    setSystemLoading(true);

    try {
      let data = {
        location: {
          location_name: currentValues?.locationName,
          location_code: currentValues?.locationCode,
          person_name: currentValues?.picName,
          person_contact: currentValues?.contactno,
          person_email: currentValues?.email,
          address_1: currentValues?.address,
          address_2: currentValues?.address2,
          postcode: currentValues?.postcode,
          state_id: currentValues?.stateId,
          country_id: currentValues?.countryId,
          area_id: currentValues?.areaId,
          is_default: currentValues?.defaultStatus ? 1 : 0,
          status: currentValues?.status ? 1 : 0,
        },
      };

      const response = await create({ data });

      promptMessage(true, `${response?.message}`);
      navigate(getRoute('settings/locations'));
    } catch (error) {
      promptMessage(false, `Action Failed (${error?.message})`);
    }

    setSystemLoading(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const { values, errors, touched, handleChange, setFieldValue } = formik;

  useEffect(() => {
    fetchDataCountry();
  }, []);

  useEffect(() => {
    if (formik?.values?.countryId) {
      if (dataStates.length) {
        if (dataStates[0].country_id !== formik.values.countryId) {
          fetchDataStates(formik.values.countryId);
        }
      } else {
        fetchDataStates(formik.values.countryId);
      }
    }

    if (formik?.values?.stateId) {
      if (dataAreas.length) {
        if (dataAreas[0].state_id !== formik.values.stateId) {
          fetchDataAreas(formik.values.stateId);
        }
      } else {
        fetchDataAreas(formik.values.stateId);
      }
    }
  }, [formik.values]);

  const checkPostcodeExists = () => {
    const checkExists = getPostcodesArray().find((i) => i === values.postcode);

    if (checkExists) return values.postcode;

    return '';
  };

  const getPostcodesArray = () => {
    let postcodeStringCheck = dataAreas.filter((i) => i.id === formik.values.areaId);

    if (postcodeStringCheck.length <= 0) return [];

    let postcodeString = postcodeStringCheck.length >= 0 ? postcodeStringCheck[0].postcode_string : '';

    let postcodeArray = postcodeString.includes(',') ? postcodeString.split(',') : [postcodeString];

    if (postcodeArray.length <= 0) return [];

    return postcodeArray;
  };

  const RenderForm = () => {
    if (!values) return <></>;

    return (
      <form onSubmit={formik.handleSubmit}>
        <StyledCard>
          <Grid container spacing={0.5}>
            <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
              <CommonSectionHeader title={t('warehouseLocation')} subTitle={t(`basicInformationForWarehouse`)} />
            </Grid>

            <Grid item xs={12} sm={12} md={8}>
              <Grid container spacing={0.5}>
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={6}
                  sx={{
                    display: 'center',
                  }}
                >
                  <FormControlLabel
                    sx={{
                      marginLeft: '1px',
                    }}
                    control={
                      <Switch
                        name="packageStatus"
                        checked={Boolean(values?.status)}
                        onChange={(event, checked) => {
                          setFieldValue('status', checked);
                        }}
                      />
                    }
                    label={Boolean(values?.status) ? t('enabled') : t('disabled')}
                  />
                </Grid>
                {/* <Grid item xs={6} sm={6} md={6}
                                    sx={{
                                        display:'center',
                                    }}>
                                    <FormControlLabel 
                                        sx={{
                                            marginLeft:'1px'
                                        }}
                                        control={
                                            <Switch 
                                                name="packageStatus" 
                                                checked={Boolean(values?.defaultStatus)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("defaultStatus", checked);
                                                }}
                                            />
                                        }     
                                        label={
                                            Boolean(values?.defaultStatus)? "Default" : "Not Default"
                                        }
                                    />
                                </Grid> */}

                <Grid item xs={12} sm={12} md={6}>
                  <></>
                </Grid>

                <Grid item xs={12} sm={12} md={4}>
                  <TextField
                    margin="dense"
                    size="small"
                    autoFocus
                    fullWidth
                    name={'locationName'}
                    label={t('locationName')}
                    type="text"
                    variant="outlined"
                    value={values.locationName}
                    onChange={handleChange}
                    error={touched.locationName && Boolean(errors.locationName)}
                    helperText={touched.locationName && errors.locationName}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={2}>
                  <TextField
                    margin="dense"
                    size="small"
                    autoFocus
                    fullWidth
                    name={'locationCode'}
                    label={t('locationCode')}
                    type="text"
                    variant="outlined"
                    value={values.locationCode}
                    onChange={handleChange}
                    error={touched.locationCode && Boolean(errors.locationCode)}
                    helperText={touched.locationCode && errors.locationCode}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'picName'}
                    label={t('personInChargeName')}
                    type="text"
                    variant="outlined"
                    value={values.picName}
                    onChange={handleChange}
                    error={touched.picName && Boolean(errors.picName)}
                    helperText={touched.picName && errors.picName}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'contactno'}
                    label={t('contact')}
                    type="text"
                    variant="outlined"
                    value={values.contactno}
                    onChange={handleChange}
                    error={touched.contactno && Boolean(errors.contactno)}
                    helperText={touched.contactno && errors.contactno}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    name={'email'}
                    label={t('email')}
                    type="text"
                    variant="outlined"
                    value={values.email}
                    onChange={handleChange}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>

            <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
              <CommonSectionHeader title={t('address')} subTitle={t(`preciseLocationInformation`)} />
            </Grid>
            <Grid item xs={12} sm={12} md={8}>
              <Grid container spacing={0.5}>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    rows={2}
                    multiline
                    name={'address'}
                    label={t('address1')}
                    type="text"
                    variant="outlined"
                    value={values.address}
                    onChange={handleChange}
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    margin="dense"
                    size="small"
                    fullWidth
                    rows={2}
                    multiline
                    name={'address2'}
                    label={t('address2')}
                    type="text"
                    variant="outlined"
                    value={values.address2}
                    onChange={handleChange}
                    error={touched.address2 && Boolean(errors.address2)}
                    helperText={touched.address2 && errors.address2}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    error={touched.countryId && Boolean(errors.countryId)}
                  >
                    <InputLabel id="country">{t('country')}</InputLabel>
                    <Select
                      labelId="countryId"
                      label={t('country')}
                      name={'countryId'}
                      onChange={(event) => {
                        setFieldValue(`countryId`, event.target.value);
                        setFieldValue(`stateId`, '');
                        setFieldValue(`areaId`, '');
                        setDataStates([]);
                        setDataAreas([]);
                      }}
                      value={checkValueExists({
                        dataSearch: dataCountries,
                        valueToCompare: values.countryId,
                      })}
                      sx={{
                        textAlign: 'left',
                      }}
                    >
                      {dataCountries.map((i, index) => {
                        return (
                          <MenuItem key={index} value={i.id}>
                            {i.country_name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText>{touched.countryId && errors.countryId}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl margin="dense" fullWidth size="small" error={touched.stateId && Boolean(errors.stateId)}>
                    <InputLabel id="state">{t('state')}</InputLabel>
                    <Select
                      labelId="stateId"
                      label={t('state')}
                      name={'stateId'}
                      onChange={(event) => {
                        setFieldValue(`stateId`, event.target.value);
                        setFieldValue(`areaId`, '');
                        setDataAreas([]);
                      }}
                      value={checkValueExists({
                        dataSearch: dataStates,
                        valueToCompare: values.stateId,
                      })}
                      sx={{
                        textAlign: 'left',
                      }}
                    >
                      {dataStates.map((i, index) => {
                        return (
                          <MenuItem key={index} value={i.id}>
                            {i.state_name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText>{touched.stateId && errors.stateId}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={12} md={6}>
                  <FormControl margin="dense" fullWidth size="small" error={touched.areaId && Boolean(errors.areaId)}>
                    <InputLabel id="area">{t('area')}</InputLabel>
                    <Select
                      labelId="areaId"
                      label={t('area')}
                      name={'areaId'}
                      onChange={handleChange}
                      value={checkValueExists({
                        dataSearch: dataAreas,
                        valueToCompare: values.areaId,
                      })}
                      sx={{
                        textAlign: 'left',
                      }}
                    >
                      {dataAreas.map((i, index) => {
                        return (
                          <MenuItem key={index} value={i.id}>
                            {i.area_name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText>{touched.areaId && errors.areaId}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    error={touched.postcode && Boolean(errors.postcode)}
                  >
                    <InputLabel id="postcode">{t('postcode')}</InputLabel>
                    <Select
                      labelId="postcode"
                      label={t('postcode')}
                      name={'postcode'}
                      onChange={handleChange}
                      // value={
                      //     values.postcode
                      // }
                      value={checkPostcodeExists()}
                      sx={{
                        textAlign: 'left',
                      }}
                    >
                      {getPostcodesArray().length ? (
                        getPostcodesArray().map((i, index) => {
                          return (
                            <MenuItem key={index} value={i}>
                              {i}
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem value={''}>{null}</MenuItem>
                      )}
                    </Select>
                    <FormHelperText>{touched.postcode && errors.postcode}</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </StyledCard>

        <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
          <Grid item md={12} textAlign={'right'}>
            <LoadingButton
              type="submit"
              // fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
              }}
              loading={isLoading}
            >
              {t('saveChanges')}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    );
  };

  return (
    <Box>
      {getHtmlHead({ routeType, title: headTitles.newLocation })}

      <CommonBreadcumbs
        routeType={routeType}
        pageTitle={t('newLocation')}
        breadcrumbs={[
          {
            displayText: t('locations'),
            props: {
              href: getRoute('settings/locations'),
            },
          },
          {
            displayText: t('newLocation'),
            props: {
              color: 'secondary.main',
            },
          },
        ]}
      />

      {RenderForm()}
    </Box>
  );
}
